import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { addHours } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { FormFieldInput } from '@/shared/components/custom/form-field';
import { RichTextEditor } from '@/shared/components/custom/rich-text-editor';
import { useDateFormat } from '@/shared/hooks/useDateFormat';
import { useToast } from '@/shared/hooks/useToast';
import { useUIStore } from '@/core/stores/ui.store';
import { useCreateCalendarEvent } from '../hooks/useCreateCalendarEvent';
import { createCalendarEventSchema } from '../schemas';
import type { CalendarEventFormValues } from '../schemas';
import { toCalendarEventDateInput } from '../utils/calendar-event-date-input';
import { externalCalendarEventToCalendarEvent } from '../adapters';
import { ModalityPicker } from './ModalityPicker';
import { AttendeesPicker } from './AttendeesPicker';
import { EventDatePicker } from './EventDatePicker';
import type { CalendarEvent, CalendarEventAttendee } from '../types';

interface CreateEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultSlot?: { start: Date; end: Date } | null;
  /** Called with the newly-created event right after a successful create — used to open its ficha. */
  onCreated?: (event: CalendarEvent) => void;
}

function buildDefaultValues(): CalendarEventFormValues {
  return { title: '', location: '', modality: null, description: '' };
}

export function CreateEventDialog({
  open,
  onOpenChange,
  defaultSlot,
  onCreated,
}: CreateEventDialogProps) {
  const { t } = useTranslation('calendar');
  const { dateFormat, timeFormat } = useDateFormat();
  const { toast } = useToast();
  const timezone = useUIStore((s) => s.timezone);
  const createEvent = useCreateCalendarEvent();
  const [attendees, setAttendees] = useState<CalendarEventAttendee[]>([]);

  // Date/time/all-day is plain state driven by `EventDatePicker` (same
  // component the ficha uses) — `type` (milestone/time-entry) is derived from
  // `allDay` on submit rather than asked as a separate field.
  const [start, setStart] = useState(() => defaultSlot?.start ?? new Date());
  const [end, setEnd] = useState(() => defaultSlot?.end ?? addHours(new Date(), 1));
  const [allDay, setAllDay] = useState(false);

  const { control, handleSubmit, reset } = useForm<CalendarEventFormValues>({
    resolver: zodResolver(createCalendarEventSchema(t)),
    defaultValues: buildDefaultValues(),
  });

  // Sincroniza los valores por defecto cada vez que se abre el diálogo — hecho
  // en el cuerpo del render (patrón "adjusting state on prop change" de React),
  // no en un useEffect, para no disparar el lint de set-state-in-effect.
  const [wasOpen, setWasOpen] = useState(false);
  if (open && !wasOpen) {
    setWasOpen(true);
    reset(buildDefaultValues());
    setAttendees([]);
    setStart(defaultSlot?.start ?? new Date());
    setEnd(defaultSlot?.end ?? addHours(new Date(), 1));
    setAllDay(false);
  } else if (!open && wasOpen) {
    setWasOpen(false);
  }

  // modal={false} + manual scroll lock: ModalityPicker/AttendeesPicker/EventDatePicker
  // open Popovers nested inside this Dialog — same Radix workaround as TaskDialog
  // (radix-ui/primitives#1159), otherwise those Popovers lose mouse-wheel scroll.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  async function onSubmit(values: CalendarEventFormValues) {
    const payload = {
      title: values.title,
      type: allDay ? ('milestone' as const) : ('time-entry' as const),
      allDay,
      start: toCalendarEventDateInput(start, allDay, timezone),
      end: toCalendarEventDateInput(end, allDay, timezone),
      location: values.location || null,
      modality: values.modality ?? null,
      description: values.description || null,
      attendeeUserIds: attendees
        .map((a) => a.userId)
        .filter((id): id is string => !!id)
        .map(Number),
    };

    try {
      const created = await createEvent.mutateAsync(payload);
      toast.success({
        title: t('createEvent.toast.created'),
        description: t('createEvent.toast.createdDesc'),
      });
      onOpenChange(false);
      onCreated?.(externalCalendarEventToCalendarEvent(created));
    } catch {
      // global handler shows error toast
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        className="flex max-h-[85vh] flex-col sm:max-w-md"
        data-testid="calendar-create-event-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('createEvent.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('createEvent.title')}</DialogDescription>
        </DialogHeader>

        <form
          id="create-event-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-1 flex-col gap-4 overflow-y-auto px-1"
        >
          <FormFieldInput
            control={control}
            name="title"
            label={t('createEvent.fields.title')}
            floatingLabel
            data-testid="calendar-event-title-input"
          />

          <div className="flex flex-col gap-1.5">
            <Label className="text-sm">{t('eventFicha.dateTimeLabel')}</Label>
            <EventDatePicker
              start={start}
              end={end}
              allDay={allDay}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
              onSave={(nextStart, nextEnd, nextAllDay) => {
                setStart(nextStart);
                setEnd(nextEnd);
                setAllDay(nextAllDay);
              }}
            />
          </div>

          <FormFieldInput
            control={control}
            name="location"
            label={t('eventFicha.locationLabel')}
            floatingLabel
            data-testid="calendar-event-location-input"
          />

          <Controller
            control={control}
            name="modality"
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <Label className="text-sm">{t('eventFicha.modality.label')}</Label>
                <ModalityPicker modality={field.value ?? null} onSelect={field.onChange} />
              </div>
            )}
          />

          <div className="flex flex-col gap-1.5">
            <Label className="text-sm">{t('eventFicha.attendees.label')}</Label>
            <AttendeesPicker attendees={attendees} onChange={setAttendees} />
          </div>

          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <Label className="text-sm">{t('eventFicha.descriptionLabel')}</Label>
                <div className="rounded-lg border border-input bg-background">
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('eventFicha.descriptionPlaceholder')}
                    variant="plain"
                    enableFixedToolbar
                    enableBubbleMenu={false}
                    enableFloatingMenu={false}
                    enableSlashCommands
                    className="min-h-20 px-3 py-2"
                  />
                </div>
              </div>
            )}
          />
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createEvent.isPending}
          >
            {t('createEvent.cancel')}
          </Button>
          <Button
            type="submit"
            form="create-event-form"
            disabled={createEvent.isPending}
            data-testid="calendar-event-submit"
          >
            {t('createEvent.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
